import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { jobCardService } from '../../../../services/apiService';
import { FaCheckCircle, FaSpinner, FaTimesCircle, FaEye } from 'react-icons/fa';
import { getDisplayName, getProfileDisplayPhone } from '../../../../utils/displayName';

/** Resolve customer display: real name, or phone if name is placeholder */
function getCustomerDisplay(customer) {
  if (!customer) return '—';
  const name = getDisplayName(customer);
  if (name) return name;
  const phone = getProfileDisplayPhone(customer);
  return phone || '—';
}

/** Resolve garage display: garage name from details, or user name/phone */
function getGarageDisplay(garage) {
  if (!garage) return '—';
  const garageName = garage.garageDetails?.garageName || garage.garageDetails?.name;
  if (garageName && String(garageName).trim()) return garageName.trim();
  const name = getDisplayName(garage);
  if (name) return name;
  const phone = getProfileDisplayPhone(garage);
  return phone || '—';
}

function truncate(str, len = 35) {
  const s = (str || '').toString().trim();
  return s.length <= len ? s : s.slice(0, len) + '…';
}

// Compute estimate totals in a consistent way (labor + parts with tax)
function getEstimateTotals(job) {
  const laborItems = job?.estimate?.laborItems || [];
  const parts = job?.estimate?.parts || [];

  const laborSub = laborItems.reduce(
    (sum, li) => sum + (Number(li.hours) || 0) * (Number(li.rate) || 0),
    0
  );

  const partsSub = parts.reduce((sum, p) => {
    const qty = Number(p.quantity || 1);
    const unit = Number(p.price ?? 0);
    const basePrice = unit > 0 ? unit : Number(p.unitPrice ?? 0);
    return sum + basePrice * qty;
  }, 0);

  const subtotal = laborSub + partsSub;
  const tax = subtotal * 0.18;
  const total = subtotal + tax;

  return { subtotal, tax, total };
}

// Map backend status to high-level workflow stage (for display)
function getWorkflowStage(status) {
  switch (status) {
    case 'Created':
      return 'INTAKE / DIAGNOSIS';
    case 'PendingCustomerApproval':
      return 'QUOTATION & CUSTOMER APPROVAL';
    case 'CustomerApproved':
    case 'PartsOrdered':
      return 'IN_PROGRESS (Repair Prep)';
    case 'InProgress':
      return 'IN_PROGRESS (Repair Work)';
    case 'ReadyForQC':
    case 'QCRejected':
    case 'QCApproved':
      return 'QUALITY_CHECK';
    case 'Billed':
      return 'INVOICE & PAYMENT';
    case 'Paid':
      return 'READY FOR DELIVERY';
    case 'Delivered':
      return 'DELIVERED';
    case 'Closed':
      return 'CLOSED';
    case 'CustomerRejected':
      return 'CANCELLED (Customer Rejected)';
    case 'Cancelled':
      return 'CANCELLED';
    default:
      return status || 'UNKNOWN';
  }
}

const JobCardsQC = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('ReadyForQC');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actingId, setActingId] = useState('');
  const [rejectModal, setRejectModal] = useState({ open: false, jobId: '', reason: '' });

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const data = await jobCardService.getAdmin(statusFilter ? { status: statusFilter } : {});
      const list = Array.isArray(data) ? data : (data?.jobCards ?? data?.jobs ?? []);
      setJobs(Array.isArray(list) ? list : []);
    } catch (e) {
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, [statusFilter]);

  const approveQC = async (id) => {
    try {
      setActingId(id);
      await jobCardService.updateStatus(id, 'QCApproved', { note: 'QC approved' });
      await fetchJobs();
    } catch (e) {
      alert(e.message || 'Failed to approve QC');
    } finally {
      setActingId('');
    }
  };

  const openReject = (jobId) => {
    setRejectModal({ open: true, jobId, reason: '' });
  };

  const confirmRejectQC = async () => {
    const { jobId, reason } = rejectModal;
    const trimmed = (reason || '').trim();
    if (trimmed.length < 3) {
      alert('Please enter a rejection reason (at least 3 characters).');
      return;
    }
    try {
      setActingId(jobId);
      await jobCardService.updateStatus(jobId, 'QCRejected', { rejectionReason: trimmed, note: trimmed });
      setRejectModal({ open: false, jobId: '', reason: '' });
      await fetchJobs();
    } catch (e) {
      alert(e.message || 'Failed to reject QC');
    } finally {
      setActingId('');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900">QC Jobs</h1>
          <p className="text-sm text-gray-500">Approve completed repairs before billing</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
          >
            <option value="ReadyForQC">ReadyForQC</option>
            <option value="QCRejected">QCRejected</option>
            <option value="QCApproved">QCApproved</option>
            <option value="Billed">Billed</option>
            <option value="Paid">Paid</option>
            <option value="">All</option>
          </select>
          <button onClick={fetchJobs} className="px-3 py-2 text-sm font-semibold rounded-lg bg-gray-100 hover:bg-gray-200">
            Refresh
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="py-12 text-center text-gray-500 flex items-center justify-center gap-2">
            <FaSpinner className="animate-spin" /> Loading…
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Job / Vehicle</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Customer</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Garage</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Complaint</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Est. Total</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Stage</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {jobs.map((j) => (
                  <tr key={j._id} className="hover:bg-gray-50/50 transition">
                    <td className="px-4 py-3">
                      <div className="text-sm font-semibold text-gray-900">#{(j.jobCardId || j._id || '').toString().slice(-8)}</div>
                      <div className="text-xs text-gray-500">
                        {(j.vehicle?.registrationNumber || '—').toString()} · {[j.vehicle?.make, j.vehicle?.model].filter(Boolean).join(' ') || '—'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{getCustomerDisplay(j.customerId)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{getGarageDisplay(j.garageId)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 max-w-[180px]" title={j.complaintDescription || ''}>
                      {truncate(j.complaintDescription, 30)}
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                      {(() => {
                        const { total } = getEstimateTotals(j);
                        return total > 0 ? `₹${Number(total).toLocaleString()}` : '—';
                      })()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-0.5">
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-800">
                          {getWorkflowStage(j.status)}
                        </span>
                        <span className="text-[10px] text-gray-400 font-mono">
                          {j.status}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <button
                        type="button"
                        onClick={() => navigate(`/service/job/${j._id}/jobcard`)}
                        title="View Job Card"
                        aria-label="View Job Card"
                        className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-base"
                      >
                        <FaEye size={18} className="text-gray-700" />
                      </button>
                      {j.status === 'ReadyForQC' ? (
                        <span className="inline-flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => openReject(j._id)}
                            disabled={actingId === j._id}
                            title="Reject QC"
                            aria-label="Reject QC"
                            className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-red-200 bg-white text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed text-base"
                          >
                            {actingId === j._id ? (
                              <FaSpinner size={18} className="animate-spin text-red-700" />
                            ) : (
                              <FaTimesCircle size={18} className="text-red-700" />
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => approveQC(j._id)}
                            disabled={actingId === j._id}
                            title="Approve QC"
                            aria-label="Approve QC"
                            className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-base"
                          >
                            {actingId === j._id ? (
                              <FaSpinner size={18} className="animate-spin text-white" />
                            ) : (
                              <FaCheckCircle size={18} className="text-white" />
                            )}
                          </button>
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400 align-middle">—</span>
                      )}
                    </td>
                  </tr>
                ))}
                {jobs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-10 text-center text-sm text-gray-500">
                      No jobs found.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {rejectModal.open ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 space-y-4">
            <h2 className="text-lg font-bold text-gray-900">Reject QC</h2>
            <p className="text-sm text-gray-600">
              The garage will see this reason, can update the job card and estimate, then send the job back for QC.
            </p>
            <div>
              <label htmlFor="qc-reject-reason" className="block text-xs font-semibold text-gray-700 mb-1">
                Reason <span className="text-red-600">*</span>
              </label>
              <textarea
                id="qc-reject-reason"
                value={rejectModal.reason}
                onChange={(e) => setRejectModal((m) => ({ ...m, reason: e.target.value }))}
                rows={4}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Describe what must be fixed or verified before approval…"
              />
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <button
                type="button"
                onClick={() => setRejectModal({ open: false, jobId: '', reason: '' })}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmRejectQC}
                disabled={actingId === rejectModal.jobId}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50"
              >
                {actingId === rejectModal.jobId ? (
                  <FaSpinner size={18} className="animate-spin text-white" />
                ) : (
                  <FaTimesCircle size={18} className="text-white" />
                )}
                Confirm rejection
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default JobCardsQC;

